<script setup lang="ts">
import {inject, ref} from "vue";
import {useRouter} from "vue-router";
import {useConfirm} from "primevue/useconfirm";
import {getPlan as apiGetPlan, deletePlan} from "@/Services/Admin/admin-plans";
import {PageHeading, PageLoading} from "@/Components/Global";
import {Button, Card, Chip, Icon, Menu} from "@/Components/Library";
import {PlanRestrictions} from "@/Enums";
import type dayjs from "dayjs";

const $date = inject("date") as typeof dayjs;
const router = useRouter();
const confirm = useConfirm();

const plan = ref();
const planMenuContainer = ref<Menu>();
const planMenuItems = [
    {
        label: "Edit",
        icon: "prime:pencil",
        command: () => {
            router.push({
                name: "admin.plans.edit",
                params: {plan: router.currentRoute.value.params.plan},
            });
        },
    },
    {
        label: "Delete",
        icon: "prime:trash",
        class: "text-red-600 hover:bg-red-100",
        command: () => deletePlanAction(),
    },
];

const getPlan = async () => {
    apiGetPlan(router.currentRoute.value.params.plan).then(({data}) => (plan.value = data));
};
getPlan();

const deletePlanAction = () => {
    confirm.require({
        message: "Are you sure you want to delete this plan?",
        accept: async () => {
            await deletePlan(router.currentRoute.value.params.plan);
            await router.push({name: "admin.plans.index"});
        },
        acceptClass: "bg-red-600 text-white px-2 py-1 rounded-lg hover:brightness-110",
        rejectClass: "px-2 py-1 hover:brightness-110",
    });
};

const formatPrice = (price: number) => {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
    }).format(price / 100);
};

const formatRestrictionValue = (slug: string, value: any) => {
    const restriction = PlanRestrictions[slug];
    if (restriction?.type === "boolean") {
        return value ? "Enabled" : "Disabled";
    }
    return value;
};
</script>
<template>
    <div class="mx-auto w-full max-w-5xl pb-12">
        <div class="m--container mb-3">
            <PageHeading title="Plan">
                <div class="ml-auto flex gap-2">
                    <Button
                        :icon="{
                            icon: 'pepicons-pencil:dots-y',
                            width: '24',
                        }"
                        class="!ring-0"
                        @click.stop="planMenuContainer.menu.toggle($event)"
                    />
                    <Menu ref="planMenuContainer" popup :model="planMenuItems" />
                </div>
            </PageHeading>
        </div>
        <PageLoading v-if="!plan" />
        <div v-else class="flex flex-col gap-4">
            <div class="flex flex-col gap-4 md:flex-row">
                <Card pt:body:class="h-full" pt:content:class="h-full flex items-center justify-center">
                    <div class="my-auto flex flex-col items-center justify-center p-4">
                        <div class="mb-4 flex flex-col items-center gap-2">
                            <h2 class="text-2xl font-bold">{{ plan.name }}</h2>
                            <div class="text-3xl font-bold text-green-600">{{ formatPrice(plan.price) }}</div>
                        </div>
                        <Chip
                            :label="plan.is_active ? 'Active' : 'Inactive'"
                            class="text-white"
                            :class="plan.is_active ? 'bg-green-800' : 'bg-red-800'"
                        />
                    </div>
                </Card>
                <Card>
                    <template #title>Details</template>
                    <template #content>
                        <table>
                            <tbody>
                                <tr>
                                    <td>ID</td>
                                    <td>{{ plan.id }}</td>
                                </tr>
                                <tr>
                                    <td>Name</td>
                                    <td>{{ plan.name }}</td>
                                </tr>
                                <tr>
                                    <td>Slug</td>
                                    <td>{{ plan.slug }}</td>
                                </tr>
                                <tr>
                                    <td>Description</td>
                                    <td>{{ plan.description || "-" }}</td>
                                </tr>
                                <tr>
                                    <td>Price</td>
                                    <td>{{ formatPrice(plan.price) }}</td>
                                </tr>
                                <tr>
                                    <td>Status</td>
                                    <td>{{ plan.is_active ? "Active" : "Inactive" }}</td>
                                </tr>
                                <tr>
                                    <td>Stripe Product ID</td>
                                    <td>{{ plan.stripe_product_id || "-" }}</td>
                                </tr>
                                <tr>
                                    <td>Stripe Price ID</td>
                                    <td>{{ plan.stripe_price_id || "-" }}</td>
                                </tr>
                                <tr>
                                    <td>Created At</td>
                                    <td>
                                        {{ $date(plan.created_at).format("DD/MM/YYYY HH:mm") }}
                                    </td>
                                </tr>
                                <tr>
                                    <td>Updated At</td>
                                    <td>
                                        {{ $date(plan.updated_at).format("DD/MM/YYYY HH:mm") }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </template>
                </Card>
            </div>
            <Card>
                <template #title>Restrictions</template>
                <template #content>
                    <div class="mt-2 grid grid-cols-2">
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <Icon
                                    :icon="plan.can_ask_detailed_questions ? 'mdi:check-circle' : 'mdi:close-circle'"
                                    :class="plan.can_ask_detailed_questions ? 'text-green-500' : 'text-red-500'"
                                    width="20"
                                    class="flex-none"
                                />
                                <h5>Ask Detailed Questions</h5>
                            </div>
                            <div class="flex gap-2">
                                <Icon
                                    :icon="plan.can_ask_annotation_questions ? 'mdi:check-circle' : 'mdi:close-circle'"
                                    :class="plan.can_ask_annotation_questions ? 'text-green-500' : 'text-red-500'"
                                    width="20"
                                    class="flex-none"
                                />
                                <h5>Ask Annotation Questions</h5>
                            </div>
                            <div class="flex gap-2">
                                <Icon
                                    :icon="plan.can_reuse_questions ? 'mdi:check-circle' : 'mdi:close-circle'"
                                    :class="plan.can_reuse_questions ? 'text-green-500' : 'text-red-500'"
                                    width="20"
                                    class="flex-none"
                                />
                                <h5>Reuse Questions</h5>
                            </div>
                            <div class="flex gap-2">
                                <Icon
                                    :icon="plan.can_export ? 'mdi:check-circle' : 'mdi:close-circle'"
                                    :class="plan.can_export ? 'text-green-500' : 'text-red-500'"
                                    width="20"
                                    class="flex-none"
                                />
                                <h5>Export data</h5>
                            </div>
                        </div>
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <h5>Group limit</h5>
                                <span class="rounded-full bg-sky-700 px-2 py-0.5 font-bold text-white">
                                    <Icon
                                        v-if="plan.group_limit === -1"
                                        icon="ion:infinite"
                                        width="20"
                                        class="mt-0.5 flex-none"
                                    />
                                    <span v-else>{{ plan.group_limit }}</span>
                                </span>
                            </div>
                            <div class="flex gap-2">
                                <h5>Group member limit</h5>
                                <span class="rounded-full bg-sky-700 px-2 py-0.5 font-bold text-white">
                                    <Icon
                                        v-if="plan.group_member_limit === -1"
                                        icon="ion:infinite"
                                        width="20"
                                        class="mt-0.5 flex-none"
                                    />
                                    <span v-else>{{ plan.group_member_limit }}</span>
                                </span>
                            </div>
                            <div class="flex gap-2">
                                <h5>Post limit</h5>
                                <span class="rounded-full bg-sky-700 px-2 py-0.5 font-bold text-white">
                                    <Icon
                                        v-if="plan.post_limit === -1"
                                        icon="ion:infinite"
                                        width="20"
                                        class="mt-0.5 flex-none"
                                    />
                                    <span v-else>{{ plan.post_limit }}</span>
                                </span>
                            </div>
                            <div class="flex gap-2">
                                <h5>AI tokens limit</h5>
                                <span class="rounded-full bg-sky-700 px-2 py-0.5 font-bold text-white">
                                    <Icon
                                        v-if="plan.ai_tokens_limit === -1"
                                        icon="ion:infinite"
                                        width="20"
                                        class="mt-0.5 flex-none"
                                    />
                                    <span v-else>{{ plan.ai_tokens_limit }}</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </template>
            </Card>
        </div>
    </div>
</template>
<style scoped>
td {
    @apply py-1.5 pr-8 align-top;
}

tr > td:first-child {
    @apply font-bold;
}
</style>
